import React, { useEffect } from 'react';
import { Alert, FormControlLabel, Grid2, Switch } from '@mui/material';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { editIntegration, useGetIntegrations } from '@openapi/integrations/integrations';
import IntegrationCard from './components/IntegrationCard';
import ViatorForm from './components/ViatorForm';
import { assignViatorProductCodes, validateViatorProducts } from './components/viatorProducts';
import TripAdvisorForm from './components/TripAdvisorForm';
import GA4Form from './components/GA4Form';
import FacebookForm from './components/FacebookForm';
import TikTokForm from './components/TikTokForm';
import LinkedInForm from './components/LinkedInForm';
import TwitterForm from './components/TwitterForm';
import SnapchatForm from './components/SnapchatForm';
import PinterestForm from './components/PinterestForm';
import ActiviterForm from './components/ActiviterForm';
import ExpediaForm from './components/ExpediaForm';

function EditIntegrationPage() {
    const { t } = useTranslation();
    const { showId, integrationType } = useParams();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const { notifyError, notifySuccess } = useNotify();

    // Fetch all integrations for this show
    const {
        data,
        isLoading: loading,
        error,
    } = useGetIntegrations(
        {
            optionalId: showId,
            pageSize: 100,
        },
        {
            enabled: !!showId,
        },
    );

    useEffect(() => {
        if (error) {
            navigate(`/shows/${showId}/integrations`);
            notifyError(error);
        }
    }, [error, navigate, notifyError, showId]);

    if (loading) {
        return true;
    }

    // Find the existing integration for this type
    const existingIntegration = data?.integrations?.find((i) => i.type === integrationType);

    const defaultIntegration = {
        showId: parseInt(showId, 10),
        type: integrationType,
        settings: {},
        enabled: false,
    };

    const initialValues = existingIntegration || defaultIntegration;

    const onSubmit = async (values) => {
        // Hand-built Viator products get a stable, name-derived code assigned on
        // first save (existing codes are preserved); the transient _id key is
        // stripped at the same time.
        const settings =
            values.type === 'viator' && Array.isArray(values.settings?.viatorProducts)
                ? {
                      ...values.settings,
                      viatorProducts: assignViatorProductCodes(values.settings.viatorProducts),
                  }
                : values.settings;
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editIntegration({
                        showId: values.showId,
                        type: values.type,
                        settings,
                        enabled: values.enabled,
                    });
                    navigate(`/shows/${showId}/integrations`);
                    notifySuccess(t('integrations.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const validateSchema = Yup.object().shape({
        showId: Yup.number().required(t('common.required')),
        type: Yup.string().required(t('common.required')),
        settings: Yup.mixed().test('viatorProducts', '', function validateSettings(settings) {
            if (this.parent.type !== 'viator') {
                return true;
            }
            const message = validateViatorProducts(settings?.viatorProducts);
            return message ? this.createError({ message }) : true;
        }),
    });

    // Viator: a commission group is only a filter for picking the affiliate agent,
    // so a group without an agent is incomplete. Both empty or both set are valid.
    const validate = (vals) => {
        const errors = {};
        if (vals.type === 'viator' && vals.commissionGroup && !vals.settings?.agent_user_id) {
            errors.settings = {
                agent_user_id: t('integrations.validation.affiliateAgentRequired'),
            };
        }
        return errors;
    };

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validateSchema}
            validate={validate}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, errors }) => (
                <Form>
                    <PageWithTitle
                        title={`${t('integrations.title')} - ${integrationType.charAt(0).toUpperCase() + integrationType.slice(1)}`}
                    >
                        <Grid2 container spacing={3}>
                            {/* Sidebar with Integration Card */}
                            <Grid2 size={{ xs: 12, md: 3 }}>
                                <IntegrationCard integration={values} compact>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={values.enabled}
                                                onChange={(e) =>
                                                    setFieldValue('enabled', e.target.checked)
                                                }
                                            />
                                        }
                                        label={t('integrations.columns.enabled')}
                                        labelPlacement="end"
                                        sx={{ mx: 0 }}
                                    />
                                </IntegrationCard>
                            </Grid2>

                            {/* Configuration Form */}
                            <Grid2 size={{ xs: 12, md: 9 }}>
                                <BlockWithTitle title={t('integrations.configuration')}>
                                    <Grid2
                                        container
                                        spacing={2}
                                        size={{ xs: 12 }}
                                        sx={{ textAlign: 'left' }}
                                    >
                                        {values.type === 'viator' && (
                                            <ViatorForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'tripadvisor' && (
                                            <TripAdvisorForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'ga4' && (
                                            <GA4Form
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'facebook' && (
                                            <FacebookForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'tiktok' && (
                                            <TikTokForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'linkedin' && (
                                            <LinkedInForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'twitter' && (
                                            <TwitterForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'snapchat' && (
                                            <SnapchatForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'pinterest' && (
                                            <PinterestForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.type === 'activiter' && (
                                            <ActiviterForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                showId={showId}
                                            />
                                        )}
                                        {values.type === 'expedia' && (
                                            <ExpediaForm
                                                values={values}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}

                                        {errors.settings && (
                                            <Grid2 size={{ xs: 12 }}>
                                                <Alert severity="error">{errors.settings}</Alert>
                                            </Grid2>
                                        )}

                                        {/* Action Buttons */}
                                        <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                                            <Grid2 container spacing={2} justifyContent="flex-end">
                                                <Grid2 size="auto">
                                                    <CommonButton
                                                        onClick={() =>
                                                            navigate(
                                                                `/shows/${showId}/integrations`,
                                                            )
                                                        }
                                                        label={t('common.cancel')}
                                                        buttonType="secondary"
                                                    />
                                                </Grid2>
                                                <Grid2 size="auto">
                                                    <CommonButton
                                                        disabled={isSubmitting}
                                                        type="submit"
                                                        label={t('common.save')}
                                                        buttonType="primary"
                                                    />
                                                </Grid2>
                                            </Grid2>
                                        </Grid2>
                                    </Grid2>
                                </BlockWithTitle>
                            </Grid2>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditIntegrationPage;
